import type { BrowserEngineRequest } from "../nikola-engine-evidence";
import type { NikolaActivity, NikolaMessagePart } from "./contracts";
import type { BoardStudyAnalysis, BoardStudyPosition } from "./study";

export const NIKOLA_RUN_PROTOCOL_VERSION = 1 as const;

export type NikolaRunStatus =
  "planning" | "running" | "waiting_client" | "grading" | "completed" | "blocked" | "failed" | "cancelled";

export const NIKOLA_TERMINAL_RUN_STATUSES = ["completed", "blocked", "failed", "cancelled"] as const;

export const isNikolaRunTerminal = (status: NikolaRunStatus): boolean =>
  (NIKOLA_TERMINAL_RUN_STATUSES as readonly NikolaRunStatus[]).includes(status);

export interface NikolaRunProgress {
  completed: number;
  total: number;
  label: string;
}

export const nikolaRunProgress = (completed: number, total: number, label: string): NikolaRunProgress => {
  const safeTotal = Math.max(0, Math.trunc(total));
  return {
    completed: Math.min(safeTotal, Math.max(0, Math.trunc(completed))),
    total: safeTotal,
    label,
  };
};

interface NikolaBrowserActionBase {
  id: string;
  stepId: string;
  idempotencyKey: string;
  label: string;
}

export type NikolaBrowserAction =
  | (NikolaBrowserActionBase & {
      kind: "analyze_game";
      gameId: string;
      gameRevision: string;
      analysisPreset: "regular" | "quick" | "deep";
      analysisMode: "ensure" | "reanalyze";
    })
  | (NikolaBrowserActionBase & {
      kind: "calculate_position";
      request: BrowserEngineRequest;
    });

export interface NikolaAnswer {
  text: string;
  parts: readonly NikolaMessagePart[];
  creditsCharged: number;
}

export type NikolaPublicFailureCode = "clarification_required" | "action_unavailable" | "cancelled" | "request_failed";

export interface NikolaPublicFailure {
  code: NikolaPublicFailureCode;
  retryable: boolean;
  message?: string;
}

export interface NikolaRunView {
  id: string;
  protocolVersion: typeof NIKOLA_RUN_PROTOCOL_VERSION;
  sessionId: string;
  status: NikolaRunStatus;
  objective: string;
  progress: NikolaRunProgress;
  pendingAction: NikolaBrowserAction | null;
  answer: NikolaAnswer | null;
  failure: NikolaPublicFailure | null;
  version: number;
}

export interface NikolaRunActivity extends NikolaActivity {
  activityId: string;
  sequence: number;
  detail: string;
  occurredAt: string;
}

export interface NikolaRunEventView {
  sequence: number;
  type: "planning" | "reading" | "calculating" | "answering" | "waiting" | "finished";
  label: string;
  activity?: NikolaRunActivity;
}

export interface CreateNikolaRunRequest {
  sessionId: string;
  message: string;
  requestId: string;
  context?: {
    surface: "chat" | "game_review" | "profile_suggestions" | "board_study";
    study?: BoardStudyPosition;
    studyAnalysis?: BoardStudyAnalysis;
    includeFullGameAnalysis?: boolean;
    gameId?: string;
    ply?: number;
    moveNumber?: number;
    san?: string;
  };
}

export interface SubmitNikolaBrowserResultRequest {
  stepId: string;
  idempotencyKey: string;
  actionId: string;
  leaseToken: string;
  artifactId?: string;
  gameId?: string;
  failed?: boolean;
}

export interface NikolaBrowserActionLeaseRequest {
  actionId: string;
  leaseToken: string;
}

export type NikolaBrowserActionLeaseResponse =
  | { status: "claimed"; leaseToken: string; expiresAt: string }
  | { status: "busy"; retryAfterMs: number }
  | { status: "settled" };
